/**
 * @file AccountMenu.tsx
 *
 * The admin top-bar account control: an `account_circle` button that opens a
 * right-anchored Gravity `Sidedrawer` (the panel WSB-v2 shows from its editor
 * topbar). The panel shows the signed-in caller and a "Preferences" group with
 * the language switcher ({@link AccountLanguageSwitcher}) and a Log out action.
 * Logging out goes through {@link useAuth}. We replicate only these two controls;
 * WSB-v2's Control Panel / View site / My projects rows are intentionally omitted.
 */

import { useState } from 'react';
import { Icon, Sidedrawer } from '@group.one/react-gravity';
import { useIntl } from 'react-intl';
import { useAuth } from '../../auth/auth-context';
import { adminMessages } from '../i18n/messages';
import { AccountLanguageSwitcher } from './AccountLanguageSwitcher';
import styles from './AccountMenu.module.css';

/**
 * Renders the account button and its slide-in preferences drawer.
 *
 * @returns The trigger button and the (conditionally open) account drawer.
 */
export function AccountMenu() {
  const intl = useIntl();
  const { identity, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const displayName = identity?.email ?? identity?.subject ?? '';

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        data-testid="account-menu-button"
        aria-label={intl.formatMessage(adminMessages.accountMenuLabel)}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon name="account_circle" />
      </button>

      <Sidedrawer
        isOpen={open}
        onClose={() => setOpen(false)}
        position="right"
        showCloseButton
        closeOnOverlayClick
        closeButtonAriaLabel={intl.formatMessage(adminMessages.accountClose)}
        className={styles.shell}
        contentClassName={`${styles.content} gv-flex gv-flex-col gv-gap-md`}
      >
        <div
          className="gv-flex gv-flex-col gv-gap-md"
          data-testid="account-menu-drawer"
        >
          {displayName && (
            <p
              className={`${styles.name} gv-text-md gv-text-bold gv-text-truncate gv-m-0 gv-py-md gv-px-sm`}
            >
              {displayName}
            </p>
          )}

          <section className="gv-flex gv-flex-col gv-gap-sm">
            <p className="gv-text-sm gv-text-bold gv-text-on-alternative gv-m-0 gv-px-sm gv-pb-sm">
              {intl.formatMessage(adminMessages.accountPreferences)}
            </p>

            <AccountLanguageSwitcher />

            <button
              type="button"
              className={`${styles.item} gv-flex gv-items-center gv-justify-between gv-w-full gv-p-sm`}
              data-testid="account-menu-logout"
              onClick={() => {
                setOpen(false);
                void logout();
              }}
            >
              <span className="gv-inline-flex gv-items-center gv-gap-sm gv-min-w-0">
                <Icon name="logout" size="small" />
                <span className="gv-text-sm">
                  {intl.formatMessage(adminMessages.accountLogout)}
                </span>
              </span>
            </button>
          </section>
        </div>
      </Sidedrawer>
    </>
  );
}
